import React, { useContext, useMemo, useState } from 'react';

import i18n from 'i18n-react';

import SearchModal from 'components/SearchModal';

const SearchContext = React.createContext();

/**
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children Nodes within context
 * @returns {React.ReactNode} The provider component.
 */
const SearchProvider = ({ children }) => {
  const [show, setShow] = useState(false);
  const [query, setQuery] = useState('');

  const showSearch = () => {
    setShow(true);
  };

  const hideSearch = () => setShow(false);

  const updateQuery = (value) => {
    setQuery(value);
  };

  const contextValues = useMemo(
    () => ({
      show,
      query,
      showSearch,
      updateQuery,
    }),
    [show, query],
  );

  return (
    <SearchContext.Provider value={contextValues}>
      {children}
      <SearchModal show={show} onHide={hideSearch} query={query} updateQuery={updateQuery} />
    </SearchContext.Provider>
  );
};

/**
 * @typedef {object} SearchControls
 * @property {boolean} show Whether to show the search modal. Most likely not needed, but if for whatever reason you need to know if the search modal is open, it's here.
 * @property {string} query The current search query. Most likely not needed, but if for whatever reason you need to know the current query, it's here.
 * @property {Function} showSearch Show the search modal
 * @property {Function} updateQuery Update the current search query
 */

/**
 * A hook for managing the search context. The modal is rendered (default: hidden) as part of the provider, so you don't need to worry about rendering it.
 *
 * @returns {SearchControls} The search controls
 */
const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error(
      i18n.translate('errors.contextError', {
        hook: 'useSearch',
        provider: 'SearchProvider',
      }),
    );
  }

  const { show, query, showSearch, updateQuery } = context;

  return {
    show,
    query,
    showSearch,
    updateQuery,
  };
};

export { SearchProvider, useSearch };
